import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLiveQuery } from 'dexie-react-hooks';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { db } from '../../db';
import FormRenderer from './FormRenderer';
import { getUnsupportedSchemaReason, validateAnswers } from './schema';
import { createDraft, discardDraft, retryDraft, saveDraft, stageDraft } from './drafts';
import { syncService } from '../../services/SyncService';
import { formTemplateApi } from '../../services/formTemplates';

export default function AssessmentComposer({ appointmentId, userId, onClose, onSubmitted, correctionAssessment = null }) {
  const [reloadCount, setReloadCount] = useState(0);
  const [isRetryingTemplate, setIsRetryingTemplate] = useState(false);
  const appointment = useLiveQuery(() => db.appointments.get(appointmentId), [appointmentId]);
  const families = useLiveQuery(() => db.form_templates.where('status').equals('active').toArray(), []) || [];
  const versions = useLiveQuery(() => db.form_template_versions.where('status').equals('published').toArray(), [reloadCount]);
  const isVersionsLoading = versions === undefined;
  const allDrafts = useLiveQuery(() => db.form_assessment_drafts.toArray(), []);
  const existingDraft = useMemo(() => {
    if (allDrafts === undefined) return undefined;
    const match = allDrafts.find(item =>
      item.appointment_id === appointmentId && item.author_id === userId
      && item.kind === (correctionAssessment ? 'correction' : 'initial')
      && (!correctionAssessment || item.assessment_id === correctionAssessment.id)
    );
    return match || null;
  }, [allDrafts, appointmentId, userId, correctionAssessment]);
  const [versionId, setVersionId] = useState(correctionAssessment?.template_version_id || '');
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState([]);
  const [activeSection, setActiveSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | failed
  const draftRef = useRef(null);
  const submittingRef = useRef(false);
  const lastSavedFingerprintRef = useRef(null);

  const available = useMemo(() => families.filter(family => family.active_version_id && (
    family.owner_type === 'user' && family.owner_id === userId
    || family.owner_type === 'clinic' && family.owner_id === appointment?.clinic_id
  )), [appointment?.clinic_id, families, userId]);
  const version = versions ? versions.find(item => item.id === versionId) : null;

  useEffect(() => {
    if (existingDraft === undefined || draft) return;
    if (existingDraft) {
      setVersionId(existingDraft.template_version_id);
      setDraft(existingDraft);
      lastSavedFingerprintRef.current = JSON.stringify({
        answers: existingDraft.answers || {},
        correction_reason: existingDraft.correction_reason || '',
      });
    } else if (correctionAssessment) {
      createDraft({
        appointmentId,
        templateVersionId: correctionAssessment.template_version_id,
        authorId: userId,
        correctionAssessment,
      }).then(next => {
        setVersionId(correctionAssessment.template_version_id);
        setDraft(next);
        lastSavedFingerprintRef.current = JSON.stringify({
          answers: next.answers || {},
          correction_reason: next.correction_reason || '',
        });
      }).catch(err => {
        console.error('Error in createDraft in AssessmentComposer:', err);
      });
    }
  }, [draft, existingDraft, correctionAssessment, appointmentId, userId]);

  // Non-recursive autosave based on content fingerprint
  const contentFingerprint = useMemo(() => {
    if (!draft || draft.state !== 'editing') return null;
    return JSON.stringify({
      answers: draft.answers || {},
      correction_reason: draft.correction_reason || '',
    });
  }, [draft?.answers, draft?.correction_reason, draft?.state]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!contentFingerprint || !draftRef.current || draftRef.current.state !== 'editing') return undefined;
    if (lastSavedFingerprintRef.current === contentFingerprint) return undefined;

    const timer = setTimeout(async () => {
      if (!draftRef.current || draftRef.current.state !== 'editing') return;
      setSaveStatus('saving');
      try {
        const saved = await saveDraft(draftRef.current);
        lastSavedFingerprintRef.current = contentFingerprint;
        setDraft(current => current ? { ...current, updated_at: saved.updated_at } : null);
        setSaveStatus('saved');
      } catch (err) {
        console.error('Autosave failed:', err);
        setSaveStatus('failed');
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (draftRef.current?.state === 'editing' && lastSavedFingerprintRef.current !== contentFingerprint) {
        saveDraft(draftRef.current).catch(err => console.error('Unmount save failed:', err));
      }
    };
  }, [contentFingerprint]);

  const selectVersion = async nextVersionId => {
    const next = await createDraft({
      appointmentId, templateVersionId: nextVersionId, authorId: userId,
      correctionAssessment: correctionAssessment ? {
        ...correctionAssessment, answers: correctionAssessment.answers,
      } : null,
    });
    setVersionId(nextVersionId);
    setDraft(next);
    lastSavedFingerprintRef.current = JSON.stringify({
      answers: next.answers || {},
      correction_reason: next.correction_reason || '',
    });
  };

  const submit = async () => {
    if (submittingRef.current || draft.state === 'pending') return;
    const nextErrors = validateAnswers(version.schema, draft.answers);
    if (draft.kind === 'correction' && !draft.correction_reason.trim()) {
      nextErrors.push({ path: 'correction_reason', fieldId: null, message: 'Correction reason is required' });
    }
    setErrors(nextErrors);
    if (nextErrors.length) {
      const first = nextErrors.find(item => item.fieldId);
      if (first) document.getElementById(first.fieldId)?.focus();
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const saved = await saveDraft(draft);
      const staged = await stageDraft(saved);
      setDraft(staged);
      onSubmitted?.(saved.assessment_id);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleManualRetrySave = async () => {
    if (!draftRef.current || draftRef.current.state !== 'editing') return;
    setSaveStatus('saving');
    try {
      const saved = await saveDraft(draftRef.current);
      if (contentFingerprint) {
        lastSavedFingerprintRef.current = contentFingerprint;
      }
      setDraft(current => current ? { ...current, updated_at: saved.updated_at } : null);
      setSaveStatus('saved');
    } catch (err) {
      console.error('Manual retry save failed:', err);
      setSaveStatus('failed');
    }
  };

  const unsupportedReason = useMemo(() => {
    if (!version) return null;
    return getUnsupportedSchemaReason(version.schema);
  }, [version]);

  if (!appointment) return <p>Loading appointment…</p>;

  // No templates available state
  if (!correctionAssessment && versionId === '' && available.length === 0) {
    return (
      <section className="space-y-5 bg-white p-6 rounded-xl border border-slate-200 shadow-sm" aria-label="Assessment form">
        <header className="flex items-center justify-between pb-3 border-b">
          <h2 className="text-xl font-bold">New Assessment</h2>
          {onClose && <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>}
        </header>
        <div role="alert" className="space-y-4 border border-slate-200 bg-slate-50 p-5 rounded-xl">
          <h3 className="text-lg font-semibold text-slate-800">No Assessment Templates Available</h3>
          <p className="text-sm text-slate-600">There are no custom form templates available for this appointment.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-5 bg-white p-6 rounded-xl border border-slate-200 shadow-sm" aria-label="Assessment form">
      <header className="flex items-center justify-between pb-3 border-b">
        <h2 className="text-xl font-bold">{correctionAssessment ? 'Correct Assessment' : 'New Assessment'}</h2>
        {onClose && <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>}
      </header>

      {!correctionAssessment && versionId === '' && (
        <div className="space-y-2">
          <label htmlFor="select-template-select" className="text-sm font-medium block">Assessment template</label>
          <select id="select-template-select" aria-label="Assessment template" value={versionId}
            className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            onChange={event => selectVersion(event.target.value)}>
            <option value="">Select a template</option>
            {available.map(family => <option key={family.active_version_id} value={family.active_version_id}>{family.name}</option>)}
          </select>
        </div>
      )}

      {draft && versionId && isVersionsLoading && (
        <p className="text-slate-500 py-4">Loading form template…</p>
      )}

      {draft && versionId && !isVersionsLoading && !version && (
        <div role="alert" className="space-y-4 border border-amber-300 bg-amber-50 p-5 rounded-xl">
          <h3 className="text-lg font-semibold text-amber-900">
            Missing Cached Template Version
          </h3>
          <p className="text-sm text-amber-800">
            This assessment form template version is not available on this device. Your entered answers have been safely preserved.
          </p>
          {draft.answers && Object.keys(draft.answers).length > 0 && (
            <div className="bg-white p-3 border rounded text-xs font-mono max-h-40 overflow-auto">
              {JSON.stringify(draft.answers || {}, null, 2)}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              aria-label="Retry template load"
              disabled={isRetryingTemplate}
              onClick={async () => {
                if (!versionId) return;
                setIsRetryingTemplate(true);
                try {
                  const token = localStorage.getItem('token');
                  let recovered = null;
                  if (token) {
                    try {
                      recovered = await formTemplateApi.getVersion(versionId, token);
                    } catch {
                      await syncService.pullData();
                    }
                  } else {
                    await syncService.pullData();
                  }
                  if (recovered) {
                    await db.form_template_versions.put(recovered);
                    toast.success('Template version recovered from server');
                  } else {
                    await syncService.pullData();
                  }
                  setReloadCount(c => c + 1);
                } catch {
                  toast.error('Failed to recover template version from server');
                } finally {
                  setIsRetryingTemplate(false);
                }
              }}
            >
              {isRetryingTemplate ? 'Retrying…' : 'Retry template load'}
            </Button>
            {draft.answers && Object.keys(draft.answers).length > 0 && (
              <Button size="sm" variant="outline" onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(draft.answers || {}, null, 2));
                toast.success('Answers copied to clipboard');
              }}>
                Copy answers
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-100" onClick={async () => {
              if (window.confirm('Discard this draft permanently?')) {
                await discardDraft(draft.id);
                setDraft(null);
                toast.success('Draft discarded');
                onClose?.();
              }
            }}>
              Discard draft
            </Button>
          </div>
        </div>
      )}

      {draft && version && unsupportedReason !== null && (
        <div role="alert" className="space-y-4 border border-amber-300 bg-amber-50 p-5 rounded-xl">
          <h3 className="text-lg font-semibold text-amber-900">
            Unsupported Form Template
          </h3>
          <p className="text-sm text-amber-800">
            {unsupportedReason} Your entered answers have been safely preserved.
          </p>
          <div className="bg-white p-3 border rounded text-xs font-mono max-h-40 overflow-auto">
            {JSON.stringify(draft.answers || {}, null, 2)}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(draft.answers || {}, null, 2));
              toast.success('Answers copied to clipboard');
            }}>
              Copy answers
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-100" onClick={async () => {
              if (window.confirm('Discard this draft permanently?')) {
                await discardDraft(draft.id);
                setDraft(null);
                toast.success('Draft discarded');
                onClose?.();
              }
            }}>
              Discard draft
            </Button>
          </div>
        </div>
      )}

      {draft && version && unsupportedReason === null && <>
        <div className="flex items-center justify-between text-xs text-slate-500 pb-2 border-b">
          <span>Section {activeSection + 1} of {version.schema.sections.length}</span>
          <div className="flex items-center gap-3">
            <span>
              {saveStatus === 'saving' && 'Saving on this device…'}
              {saveStatus === 'failed' && (
                <span className="text-red-600 font-medium inline-flex items-center gap-1.5">
                  Save failed on this device
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label="Retry save"
                    className="h-5 px-1.5 text-xs text-red-700 border-red-300 hover:bg-red-100"
                    onClick={handleManualRetrySave}
                  >
                    Retry
                  </Button>
                </span>
              )}
              {saveStatus !== 'saving' && saveStatus !== 'failed' && `Saved on this device at ${new Date(draft.updated_at).toLocaleTimeString()}`}
            </span>
            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 h-6 px-2 text-xs" onClick={async () => {
              if (window.confirm('Discard this assessment draft? Typed answers will be deleted.')) {
                await discardDraft(draft.id);
                setDraft(null);
                toast.success('Draft discarded');
                onClose?.();
              }
            }}>
              Discard draft
            </Button>
          </div>
        </div>

        <nav aria-label="Assessment sections" className="flex flex-wrap gap-2">
          {version.schema.sections.map((section, index) =>
            <Button key={section.id} type="button" size="sm" variant={activeSection === index ? 'default' : 'outline'} onClick={() => {
              setActiveSection(index); document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}>{index + 1}. {section.title}</Button>)}
        </nav>

        {draft.kind === 'correction' && <div className="space-y-1">
          <label htmlFor="correction-reason" className="text-sm font-medium">Correction reason *</label>
          <Input id="correction-reason" value={draft.correction_reason || ''}
            onChange={event => setDraft(current => ({ ...current, correction_reason: event.target.value }))} />
        </div>}

        <div>
          <FormRenderer schema={version.schema} answers={draft.answers || {}} errors={errors}
            readOnly={draft.state === 'failed' && draft.sync_error_status === 403}
            onChange={(fieldId, value) => setDraft(current => ({ ...current, answers: { ...(current.answers || {}), [fieldId]: value }, state: 'editing' }))} />
        </div>

        {draft.state === 'pending' && <p role="status" className="text-sm font-medium text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">Pending synchronization</p>}

        {draft.state === 'conflict' && <div role="alert" className="space-y-3 border border-red-200 bg-red-50 p-4 rounded-lg text-red-900">
          <p className="text-sm font-medium">Conflict detected: {draft.sync_error || 'This record has been modified elsewhere.'}</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              const entity = draft.kind === 'correction' ? 'form_assessment_correction' : 'form_assessment_submit';
              await syncService.resolveConflictAndRetry(draft.id, entity);
              syncService.processQueue();
              setDraft(current => ({ ...current, state: 'pending' }));
            }}>Resolve conflict & resubmit</Button>
            <Button size="sm" variant="outline" onClick={() => setDraft(current => ({ ...current, state: 'editing' }))}>Continue editing</Button>
          </div>
        </div>}

        {draft.state === 'failed' && <div role="alert" className="space-y-3 border border-red-200 bg-red-50 p-4 rounded-lg text-red-900">
          <p className="text-sm font-medium">{draft.sync_error}</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => { await retryDraft(draft.id); setDraft(current => ({ ...current, state: 'pending' })); }}>Retry</Button>
            {draft.sync_error_status !== 403 && <Button size="sm" variant="outline" onClick={() => setDraft(current => ({ ...current, state: 'editing' }))}>Continue editing</Button>}
          </div>
          {draft.sync_error_status === 403 && <p className="text-xs">Your access changed. Answers remain read-only on this device until access is restored.</p>}
        </div>}

        <div className="pt-4 border-t flex justify-end">
          <Button disabled={isSubmitting || draft.state === 'pending'} onClick={submit}>{draft.kind === 'correction' ? 'Submit correction' : 'Submit assessment'}</Button>
        </div>
      </>}
    </section>
  );
}

AssessmentComposer.propTypes = {
  appointmentId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSubmitted: PropTypes.func,
  correctionAssessment: PropTypes.object,
};
